import {
  Accordion,
  accordionItemBuilder,
  Box,
  Dropdown,
  FormField,
  Text,
  Thumbnail,
  Tooltip
} from "@wix/design-system";
import {
  AlignCenterVerticallySmall,
  AlignLeftSmall,
  AlignRightSmall,
  DropdownListSmall,
  HorizontalRatioSmall,
  ImageSmall,
  ObjectPositionLeftSmall,
  ObjectPositionRightSmall,
  ObjectPositionTopSmall,
  SquareRatioSmall,
  StackHorizontalSmall,
  StackVerticalSmall
} from "@wix/wix-ui-icons-common";
import React, { useContext } from "react";
import { RicosContext } from "./../../ricos-context/RicosContext.js";
import {
  AppEmbedData_CardStyles_Alignment,
  AppEmbedData_ImageStyles_AspectRatio,
  AppEmbedData_ImageStyles_Position,
  AppEmbedData_ImageStyles_Resizing,
  AppEmbedData_RibbonStyles_Placement
} from "./../../ricos-schema/next-schema/generated/ricos_document.js";
import { useBiNext } from "./../../_shared/bi-context-provider/bi-context-provider.js";
import { unreachable } from "./../../_shared/uncreachable.js";
import { usePanelContainerElement } from "./../../ui-components/index.js";
import { TITLE_PRICE_LAYOUT } from "../viewer/vertical-embed-styles.js";
const LayoutTab = ({ nodeId, modalId, pmNodeAttrs, patchPmNodeAttrs }) => {
  const { t, languageDir } = useContext(RicosContext);
  const isRTL = languageDir === "rtl";
  const containerEl = usePanelContainerElement();
  const logBi = useBiNext({ modalId, pluginId: "verticalEmbed", nodeId });
  const position = pmNodeAttrs?.imageStyles?.imagePosition || AppEmbedData_ImageStyles_Position.START;
  const ratio = pmNodeAttrs?.imageStyles?.aspectRatio || AppEmbedData_ImageStyles_AspectRatio.SQUARE;
  const resizing = pmNodeAttrs?.imageStyles?.resizing || AppEmbedData_ImageStyles_Resizing.FILL;
  const alignment = pmNodeAttrs?.cardStyles?.alignment || AppEmbedData_CardStyles_Alignment.START;
  const titlePriceLayout = pmNodeAttrs?.cardStyles?.titlePriceLayout || TITLE_PRICE_LAYOUT.STACKED;
  const ribbonPlacement = pmNodeAttrs?.ribbonStyles?.ribbonPlacement || AppEmbedData_RibbonStyles_Placement.PRODUCT_INFO;
  const updateStyles = (updates) => {
    const imageUpdates = {};
    const cardUpdates = {};
    const ribbonUpdates = {};
    Object.entries(updates).forEach(([key, value]) => {
      const styleKey = key;
      switch (styleKey) {
        case "position":
          imageUpdates.imagePosition = value.toUpperCase();
          break;
        case "ratio":
          imageUpdates.aspectRatio = value.toUpperCase();
          break;
        case "resizing":
          imageUpdates.resizing = value.toUpperCase();
          break;
        case "alignment":
          cardUpdates.alignment = value.toUpperCase();
          break;
        case "titlePriceLayout":
          cardUpdates.titlePriceLayout = value.toUpperCase();
          break;
        case "ribbonPlacement":
          ribbonUpdates.ribbonPlacement = value.toUpperCase();
          break;
        default:
          unreachable(styleKey);
          return null;
      }
    });
    patchPmNodeAttrs({
      imageStyles: {
        ...pmNodeAttrs?.imageStyles,
        ...imageUpdates
      },
      cardStyles: {
        ...pmNodeAttrs?.cardStyles,
        ...cardUpdates
      },
      ribbonStyles: {
        ...pmNodeAttrs?.ribbonStyles,
        ...ribbonUpdates
      }
    });
  };
  return /* @__PURE__ */ React.createElement(
    Accordion,
    {
      hideShadow: true,
      skin: "light",
      size: "medium",
      multiple: true,
      items: [
        accordionItemBuilder({
          initiallyOpen: true,
          title: /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("Embed_Vertical_Layout_Panel_Image_Section")),
          children: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3" }, /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Position_Label"),
              labelPlacement: "left",
              labelWidth: "1fr"
            },
            /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "SP1", align: "left" }, /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: isRTL ? t("Embed_Vertical_Layout_Panel_Position_Right") : t("Embed_Vertical_Layout_Panel_Position_Left"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: position === AppEmbedData_ImageStyles_Position.START,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_POSITION_START" });
                    updateStyles({ position: AppEmbedData_ImageStyles_Position.START });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: isRTL ? /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ObjectPositionRightSmall, null)) : /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ObjectPositionLeftSmall, null)),
                  dataHook: "VERTICAL_EMBED_POSITION_START"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Position_Top"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: position === AppEmbedData_ImageStyles_Position.TOP,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_POSITION_TOP" });
                    updateStyles({ position: AppEmbedData_ImageStyles_Position.TOP });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ObjectPositionTopSmall, null)),
                  dataHook: "VERTICAL_EMBED_POSITION_TOP"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: isRTL ? t("Embed_Vertical_Layout_Panel_Position_Left") : t("Embed_Vertical_Layout_Panel_Position_Right"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: position === AppEmbedData_ImageStyles_Position.END,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_POSITION_END" });
                    updateStyles({ position: AppEmbedData_ImageStyles_Position.END });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: isRTL ? /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ObjectPositionLeftSmall, null)) : /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ObjectPositionRightSmall, null)),
                  dataHook: "VERTICAL_EMBED_POSITION_END"
                }
              )
            ))
          ), /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Ratio_Label"),
              labelPlacement: "left",
              labelWidth: "1fr"
            },
            /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "SP1", align: "left" }, /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Ratio_Square"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: ratio === AppEmbedData_ImageStyles_AspectRatio.SQUARE,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_RATIO_SQUARE" });
                    updateStyles({ ratio: AppEmbedData_ImageStyles_AspectRatio.SQUARE });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(SquareRatioSmall, null)),
                  dataHook: "VERTICAL_EMBED_RATIO_SQUARE"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Ratio_Rectangle"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: ratio === AppEmbedData_ImageStyles_AspectRatio.RECTANGLE,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_RATIO_RECTANGLE" });
                    updateStyles({ ratio: AppEmbedData_ImageStyles_AspectRatio.RECTANGLE });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(HorizontalRatioSmall, null)),
                  dataHook: "VERTICAL_EMBED_RATIO_RECTANGLE"
                }
              )
            ))
          ), /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Resizing_Label"),
              labelPlacement: "left",
              labelWidth: "1fr",
              inputWidth: "116px"
            },
            /* @__PURE__ */ React.createElement(
              Dropdown,
              {
                size: "small",
                placeholder: t("Embed_Vertical_Layout_Panel_Resizing_Placeholder"),
                selectedId: resizing,
                onSelect: ({ id }) => {
                  logBi({ dataHook: "VERTICAL_EMBED_RESIZING_CHANGE", value: id });
                  updateStyles({ resizing: id });
                },
                options: [
                  {
                    id: "FILL",
                    value: t("Embed_Vertical_Layout_Panel_Resizing_Fill")
                  },
                  {
                    id: "FIT",
                    value: t("Embed_Vertical_Layout_Panel_Resizing_Fit")
                  }
                ],
                dataHook: "VERTICAL_EMBED_RESIZING_DROPDOWN"
              }
            )
          ))
        }),
        accordionItemBuilder({
          initiallyOpen: true,
          title: /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("Embed_Vertical_Layout_Panel_Information_Section")),
          children: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3" }, /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Alignment_Label"),
              labelPlacement: "left",
              labelWidth: "1fr"
            },
            /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "SP1", align: "left" }, /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Alignment_Left"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: alignment === AppEmbedData_CardStyles_Alignment.START,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_ALIGNMENT_LEFT" });
                    updateStyles({ alignment: AppEmbedData_CardStyles_Alignment.START });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(AlignLeftSmall, null)),
                  dataHook: "VERTICAL_EMBED_ALIGNMENT_LEFT"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Alignment_Center"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: alignment === "CENTER",
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_ALIGNMENT_CENTER" });
                    updateStyles({ alignment: "CENTER" });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(AlignCenterVerticallySmall, null)),
                  dataHook: "VERTICAL_EMBED_ALIGNMENT_CENTER"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Alignment_Right"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: alignment === AppEmbedData_CardStyles_Alignment.END,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_ALIGNMENT_RIGHT" });
                    updateStyles({ alignment: AppEmbedData_CardStyles_Alignment.END });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(AlignRightSmall, null)),
                  dataHook: "VERTICAL_EMBED_ALIGNMENT_RIGHT"
                }
              )
            ))
          ), /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Title_Price_Label"),
              labelPlacement: "left",
              labelWidth: "1fr"
            },
            /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "SP1", align: "left" }, /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Title_Price_Stacked"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: titlePriceLayout === TITLE_PRICE_LAYOUT.STACKED,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_TITLE_PRICE_STACKED" });
                    updateStyles({ titlePriceLayout: TITLE_PRICE_LAYOUT.STACKED });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(StackVerticalSmall, null)),
                  dataHook: "VERTICAL_EMBED_TITLE_PRICE_STACKED"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Title_Price_SideBySide"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: titlePriceLayout === "SIDE_BY_SIDE",
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_TITLE_PRICE_SIDE_BY_SIDE" });
                    updateStyles({ titlePriceLayout: "SIDE_BY_SIDE" });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(StackHorizontalSmall, null)),
                  dataHook: "VERTICAL_EMBED_TITLE_PRICE_SIDE_BY_SIDE"
                }
              )
            ))
          ), /* @__PURE__ */ React.createElement(
            FormField,
            {
              label: t("Embed_Vertical_Layout_Panel_Ribbon_Placement_Label"),
              labelPlacement: "left",
              labelWidth: "1fr"
            },
            /* @__PURE__ */ React.createElement(Box, { direction: "horizontal", gap: "SP1", align: "left" }, /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Ribbon_Placement_OnImage"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: ribbonPlacement === AppEmbedData_RibbonStyles_Placement.IMAGE,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_RIBBON_PLACEMENT_ON_IMAGE" });
                    updateStyles({
                      ribbonPlacement: AppEmbedData_RibbonStyles_Placement.IMAGE
                    });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(ImageSmall, null)),
                  dataHook: "VERTICAL_EMBED_RIBBON_PLACEMENT_ON_IMAGE"
                }
              )
            ), /* @__PURE__ */ React.createElement(
              Tooltip,
              {
                size: "small",
                inline: true,
                content: t("Embed_Vertical_Layout_Panel_Ribbon_Placement_AboveInfo"),
                appendTo: containerEl
              },
              /* @__PURE__ */ React.createElement(
                Thumbnail,
                {
                  size: "tiny",
                  selected: ribbonPlacement === AppEmbedData_RibbonStyles_Placement.PRODUCT_INFO,
                  onClick: () => {
                    logBi({ dataHook: "VERTICAL_EMBED_RIBBON_PLACEMENT_ABOVE_INFO" });
                    updateStyles({
                      ribbonPlacement: AppEmbedData_RibbonStyles_Placement.PRODUCT_INFO
                    });
                  },
                  noPadding: true,
                  width: 30,
                  height: 30,
                  contentAlignment: "center",
                  textPosition: "outside",
                  image: /* @__PURE__ */ React.createElement(Box, { height: "100%", width: "100%", align: "center", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(DropdownListSmall, null)),
                  dataHook: "VERTICAL_EMBED_RIBBON_PLACEMENT_ABOVE_INFO"
                }
              )
            ))
          ))
        })
      ]
    }
  );
};
export {
  LayoutTab
};
//# sourceMappingURL=LayoutTab.js.map
